/** Public B2B landing — partner application + sign-in entry. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, ArrowRight, CheckCircle2 } from "lucide-react";
import axios from "axios";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
const FIELD = "w-full h-11 px-3 bg-[#050505] border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:border-[#C5A059] outline-none";

export default function B2BLanding() {
  const [f, setF] = useState({ company_name: "", oib: "", contact_name: "", email: "", phone: "", est_monthly_rides: "1-10", notes: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_name || !f.contact_name || !f.email || !f.phone) return toast.error("Company, contact, email and phone are required.");
    setBusy(true);
    try {
      await api.post("/b2b/apply", f);
      setSent(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg).join(" · ") : "") || "Could not send your application.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="b2b-landing" className="min-h-screen bg-[#050505] text-zinc-100 px-6 py-16">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-[#C5A059]">← Juke Transfers</Link>
        <div className="mt-8 flex items-center gap-3">
          <Building2 className="w-8 h-8 text-[#C5A059]" />
          <h1 className="font-display text-4xl sm:text-5xl font-light text-white">Hotels &amp; Agencies</h1>
        </div>
        <p className="mt-4 text-zinc-400 max-w-xl">
          Book premium transfers for your guests in seconds, follow every ride live, and settle
          by monthly invoice — or let guests pay the driver directly. Your choice.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#apply" data-testid="b2b-apply-cta" className="h-12 px-6 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-semibold text-sm uppercase tracking-[0.15em] flex items-center gap-2">
            Become a B2B Partner <ArrowRight className="w-4 h-4" />
          </a>
          <Link to="/b2b/login" data-testid="b2b-signin-cta" className="h-12 px-6 rounded-full border border-white/15 hover:border-[#C5A059] text-zinc-200 hover:text-[#C5A059] text-sm uppercase tracking-[0.15em] flex items-center">
            Already a Partner? Sign In
          </Link>
        </div>

        <div id="apply" className="mt-14 rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 sm:p-8">
          {sent ? (
            <div data-testid="b2b-apply-success" className="text-center py-10">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-xl text-white font-light">Application received!</h2>
              <p className="text-sm text-zinc-400 mt-2 max-w-md mx-auto">
                Thank you — our team will review your application and email your partner
                login credentials within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="b2b-apply-form">
              <h2 className="text-sm uppercase tracking-[0.2em] text-[#C5A059]">Partner application</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1.5">Company / Hotel name *</label>
                  <input data-testid="b2b-company" value={f.company_name} onChange={set("company_name")} className={FIELD} placeholder="Hotel Adriatic d.o.o." /></div>
                <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1.5">OIB</label>
                  <input data-testid="b2b-oib" value={f.oib} onChange={set("oib")} className={FIELD} placeholder="11 digits" /></div>
                <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1.5">Contact person *</label>
                  <input data-testid="b2b-contact" value={f.contact_name} onChange={set("contact_name")} className={FIELD} placeholder="Front-office manager" /></div>
                <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1.5">Email *</label>
                  <input data-testid="b2b-email" type="email" value={f.email} onChange={set("email")} className={FIELD} placeholder="reception@hotel.com" /></div>
                <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1.5">Phone *</label>
                  <input data-testid="b2b-phone" value={f.phone} onChange={set("phone")} className={FIELD} placeholder="+385 …" /></div>
                <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1.5">Estimated monthly rides</label>
                  <select data-testid="b2b-rides" value={f.est_monthly_rides} onChange={set("est_monthly_rides")} className={FIELD}>
                    <option value="1-10">1 – 10</option><option value="10-30">10 – 30</option><option value="30+">30+</option>
                  </select></div>
              </div>
              <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1.5">Notes</label>
                <textarea data-testid="b2b-notes" value={f.notes} onChange={set("notes")} rows={3} className="w-full px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:border-[#C5A059] outline-none" placeholder="Anything we should know…" /></div>
              <button type="submit" data-testid="b2b-apply-submit" disabled={busy}
                className="w-full h-12 rounded-xl bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-60 text-black font-semibold text-sm uppercase tracking-[0.15em]">
                {busy ? "Sending…" : "Submit application"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
